import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Upload } from 'lucide-react';
import api from '../../utils/api';
import { useTenant } from '../../context/TenantContext';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';

const Documents = () => {
  const { activeTenant } = useTenant();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState({});

  const loadDocuments = async () => {
    if (!activeTenant) return;
    try {
      setLoading(true);
      const res = await api.get(`/documents?tenant_id=${activeTenant}`);
      setDocuments(res.data || []);
    } catch (error) {
      toast.error('Error cargando documentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [activeTenant]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tenant_id', activeTenant);
    try {
      await api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Documento subido');
      loadDocuments();
    } catch (error) {
      toast.error('Error subiendo documento');
    }
  };

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return documents.filter((doc) => {
      const matchesQuery = !normalized || doc.filename?.toLowerCase().includes(normalized);
      const matchesType = typeFilter === 'all' ? true : doc.document_type === typeFilter;
      return matchesQuery && matchesType;
    });
  }, [documents, query, typeFilter]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      const aValue = sortKey === 'created_at' ? new Date(a.created_at) : (a[sortKey] || '').toString().toLowerCase();
      const bValue = sortKey === 'created_at' ? new Date(b.created_at) : (b[sortKey] || '').toString().toLowerCase();
      if (aValue < bValue) return -1 * dir;
      if (aValue > bValue) return 1 * dir;
      return 0;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const allSelected = sorted.length > 0 && sorted.every((d) => selected[d.id]);
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected({});
      return;
    }
    const next = {};
    sorted.forEach((doc) => { next[doc.id] = true; });
    setSelected(next);
  };
  const toggleRow = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleBulkCopy = () => {
    const names = sorted.filter((d) => selected[d.id]).map((d) => d.filename);
    if (!names.length) {
      toast.error('Selecciona al menos un documento');
      return;
    }
    navigator.clipboard?.writeText(names.join('\n'));
    toast.success('Nombres copiados');
  };

  const handleBulkExport = () => {
    const rows = sorted.filter((d) => selected[d.id]);
    if (!rows.length) {
      toast.error('Selecciona al menos un documento');
      return;
    }
    const header = ['filename', 'document_type', 'created_at'];
    const csv = [
      header.join(','),
      ...rows.map((d) => [d.filename, d.document_type, d.created_at].map((v) => `"${v || ''}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'documentos.csv';
    link.click();
  };

  const typeOptions = useMemo(() => {
    const types = new Set(documents.map((doc) => doc.document_type).filter(Boolean));
    return Array.from(types);
  }, [documents]);

  return (
    <div data-testid="documents-page">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h1 className="text-4xl font-heading font-bold">Documentos</h1>
          <p className="text-muted-foreground">Gestiona archivos del tenant activo</p>
        </div>
        <div>
          <input type="file" id="file-upload" className="hidden" onChange={handleUpload} />
          <Button onClick={() => document.getElementById('file-upload').click()} className="gap-2">
            <Upload className="w-4 h-4" />Subir Documento
          </Button>
        </div>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Input
            placeholder="Buscar por nombre"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-xs"
          />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="all">Todos los tipos</option>
            {typeOptions.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleBulkCopy}>Copiar nombres</Button>
            <Button variant="outline" onClick={handleBulkExport}>Exportar CSV</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium cursor-pointer" onClick={() => toggleSort('filename')}>
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium cursor-pointer" onClick={() => toggleSort('document_type')}>
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium cursor-pointer" onClick={() => toggleSort('created_at')}>
                  Fecha
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="4" className="px-6 py-4 text-center">Cargando...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan="4" className="px-6 py-4 text-center">No hay documentos</td></tr>
              ) : (
                sorted.map((doc) => (
                  <tr key={doc.id} className="border-t">
                    <td className="px-4 py-4">
                      <input type="checkbox" checked={Boolean(selected[doc.id])} onChange={() => toggleRow(doc.id)} />
                    </td>
                    <td className="px-6 py-4">{doc.filename}</td>
                    <td className="px-6 py-4">{doc.document_type}</td>
                    <td className="px-6 py-4 text-muted-foreground text-sm">{new Date(doc.created_at).toLocaleDateString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};
export default Documents;
